import { notificationsService } from '../supabase/notifications.service';
import { notificationQueueService } from '../queue/notificationQueue';
import logger from '../../utils/logger';
import { supabase } from '../../config/supabase';

/**
 * Service de synchronisation des notifications
 * Permet de détecter et corriger les incohérences entre les états locaux et distants
 */
export const notificationSyncService = {
  /**
   * Vérifie et corrige les incohérences entre les états locaux et distants
   * @returns Une promesse qui résout lorsque la synchronisation est terminée
   */
  async checkAndFixInconsistencies(): Promise<void> {
    try {
      logger.info('Vérification des incohérences de notifications...');
      
      // Récupérer les états sauvegardés dans localStorage
      const savedStates = localStorage.getItem('notification_states');
      if (!savedStates) {
        logger.info('Aucun état local trouvé, rien à synchroniser.');
        return;
      }
      
      const localStates = JSON.parse(savedStates) as Array<{ id: string, status: 'read' | 'unread' }>;
      if (localStates.length === 0) {
        logger.info('Aucun état local trouvé, rien à synchroniser.');
        return;
      }
      
      // Récupérer les notifications depuis Supabase
      const { data, error } = await supabase
        .from('notifications')
        .select('id, status, type, reminder_date')
        .in('id', localStates.map(state => state.id));
      
      if (error) {
        logger.error('Erreur lors de la récupération des notifications pour synchronisation:', error);
        return;
      }
      
      if (!data || data.length === 0) {
        logger.info('Aucune notification trouvée dans Supabase pour synchronisation.');
        return;
      }
      
      // Identifier les incohérences
      const inconsistencies = [];
      const now = new Date();
      
      for (const localState of localStates) {
        const remoteNotification = data.find(n => n.id === localState.id);
        
        // Ignorer les notifications qui n'existent plus dans Supabase
        if (!remoteNotification) continue;
        
        // Vérifier si c'est un rappel à venir
        const isUpcomingReminder = 
          remoteNotification.type === 'reminder' && 
          remoteNotification.reminder_date && 
          new Date(remoteNotification.reminder_date) > now;
        
        // Si c'est un rappel à venir, nous ne modifions pas son statut dans la base de données
        // car il doit rester dans l'onglet "upcoming" indépendamment de son statut
        if (isUpcomingReminder) {
          logger.info(`Notification ${remoteNotification.id} est un rappel à venir, pas de synchronisation nécessaire.`);
          continue;
        }
        
        // Pour les autres notifications, si l'état local est 'read' et l'état distant est 'unread',
        // nous devons synchroniser
        if (remoteNotification.status !== localState.status && localState.status === 'read') {
          inconsistencies.push({
            id: localState.id,
            localStatus: localState.status,
            remoteStatus: remoteNotification.status
          });
        }
      }
      
      logger.info(`${inconsistencies.length} incohérences détectées.`);
      
      // Corriger les incohérences
      for (const inconsistency of inconsistencies) {
        logger.info(`Correction de l'incohérence pour la notification ${inconsistency.id}: local=${inconsistency.localStatus}, remote=${inconsistency.remoteStatus}`);
        
        try {
          // Ajouter à la file d'attente pour garantir la mise à jour
          notificationQueueService.enqueue(inconsistency.id, 'markAsRead');
          
          // Tenter une mise à jour immédiate
          await supabase
            .from('notifications')
            .update({ status: 'read', updated_at: new Date().toISOString() })
            .eq('id', inconsistency.id);
            
          // Tenter également une mise à jour via RPC pour être sûr
          await supabase.rpc('force_update_notification_status', {
            notification_id: inconsistency.id,
            new_status: 'read'
          });
          
          logger.info(`Notification ${inconsistency.id} synchronisée avec succès.`);
        } catch (updateError) {
          logger.error(`Erreur lors de la synchronisation de la notification ${inconsistency.id}:`, updateError);
          // L'opération reste dans la file d'attente pour une tentative ultérieure
        }
      }
      
      // Vérifier si des rappels à venir ont été incorrectement marqués comme lus dans localStorage
      this.checkAndFixUpcomingReminders();
      
      logger.info('Vérification et correction des incohérences terminées.');
    } catch (error) {
      logger.error('Erreur lors de la vérification des incohérences:', error);
    }
  },
  
  /**
   * Vérifie et corrige les rappels à venir qui auraient été incorrectement marqués dans localStorage
   */
  async checkAndFixUpcomingReminders(): Promise<void> {
    try {
      // Récupérer toutes les notifications de type reminder avec une date future
      const now = new Date();
      const { data, error } = await supabase
        .from('notifications')
        .select('id, status, type, reminder_date')
        .eq('type', 'reminder')
        .not('reminder_date', 'is', null);
      
      if (error || !data) {
        logger.error('Erreur lors de la récupération des rappels:', error);
        return;
      }
      
      // Filtrer pour ne garder que les rappels à venir
      const upcomingReminders = data.filter(reminder => 
        reminder.reminder_date && new Date(reminder.reminder_date) > now
      );
      
      if (upcomingReminders.length === 0) {
        logger.info('Aucun rappel à venir trouvé.');
        return;
      }
      
      logger.info(`${upcomingReminders.length} rappels à venir trouvés.`);
      
      // Récupérer les états sauvegardés dans localStorage
      const savedStates = localStorage.getItem('notification_states');
      if (!savedStates) return;
      
      let localStates = JSON.parse(savedStates) as Array<{ id: string, status: 'read' | 'unread' }>;
      let hasChanges = false;
      
      // Pour chaque rappel à venir, s'assurer qu'il n'est pas marqué comme lu dans localStorage
      for (const reminder of upcomingReminders) {
        const localState = localStates.find(state => state.id === reminder.id);
        
        // Si le rappel est marqué comme lu dans localStorage, le supprimer pour qu'il utilise son statut par défaut
        if (localState) {
          logger.info(`Suppression de l'état local pour le rappel à venir ${reminder.id}`);
          localStates = localStates.filter(state => state.id !== reminder.id);
          hasChanges = true;
        }
      }
      
      // Si des changements ont été effectués, sauvegarder les états
      if (hasChanges) {
        localStorage.setItem('notification_states', JSON.stringify(localStates));
        logger.info('États des rappels à venir corrigés dans localStorage.');
      }
    } catch (error) {
      logger.error('Erreur lors de la vérification des rappels à venir:', error);
    }
  },
  
  /**
   * Vérifie si des triggers ou règles de sécurité pourraient réinitialiser les états
   * @returns Une promesse qui résout avec un rapport des problèmes potentiels
   */
  async checkForResetTriggers(): Promise<{ hasPotentialIssues: boolean, issues: string[] }> {
    try {
      logger.info('Vérification des triggers qui pourraient réinitialiser les états...');
      
      const issues: string[] = [];
      
      // Tester si une notification reste marquée comme lue après une mise à jour
      const testId = await this.createTestNotification();
      if (!testId) {
        logger.error('Impossible de créer une notification de test.');
        return { hasPotentialIssues: true, issues: ['Impossible de créer une notification de test.'] };
      }
      
      // Marquer comme lue
      await notificationsService.markAsRead(testId);
      
      // Attendre un peu pour s'assurer que la mise à jour est propagée
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Vérifier l'état
      const { data: firstCheck, error: firstCheckError } = await supabase
        .from('notifications')
        .select('status')
        .eq('id', testId)
        .single();
      
      if (firstCheckError || !firstCheck) {
        logger.error('Erreur lors de la vérification de la notification de test:', firstCheckError);
        issues.push('Erreur lors de la vérification de la notification de test.');
      } else if (firstCheck.status !== 'read') {
        logger.warn('La notification de test n\'a pas été correctement marquée comme lue.');
        issues.push('Les notifications ne sont pas correctement marquées comme lues dans la base de données.');
      }
      
      // Faire une mise à jour mineure sur la notification pour voir si cela réinitialise son statut
      const { error: updateError } = await supabase
        .from('notifications')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', testId);
      
      if (updateError) {
        logger.error('Erreur lors de la mise à jour de la notification de test:', updateError);
        issues.push('Erreur lors de la mise à jour de la notification de test.');
      }
      
      // Attendre un peu pour s'assurer que la mise à jour est propagée
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Vérifier à nouveau l'état
      const { data: secondCheck, error: secondCheckError } = await supabase
        .from('notifications')
        .select('status')
        .eq('id', testId)
        .single();
      
      if (secondCheckError || !secondCheck) {
        logger.error('Erreur lors de la seconde vérification de la notification de test:', secondCheckError);
        issues.push('Erreur lors de la seconde vérification de la notification de test.');
      } else if (secondCheck.status !== 'read') {
        logger.warn('Le statut de la notification a été réinitialisé après une mise à jour.');
        issues.push('Un trigger ou une règle de sécurité réinitialise le statut des notifications après une mise à jour.');
      }
      
      // Nettoyer
      await supabase.from('notifications').delete().eq('id', testId);
      
      return {
        hasPotentialIssues: issues.length > 0,
        issues
      };
    } catch (error) {
      logger.error('Erreur lors de la vérification des triggers:', error);
      return {
        hasPotentialIssues: true,
        issues: ['Erreur lors de la vérification des triggers: ' + (error as Error).message]
      };
    }
  },
  
  /**
   * Crée une notification de test
   * @returns L'ID de la notification créée, ou null en cas d'erreur
   */
  async createTestNotification(): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          title: 'Test de synchronisation',
          message: 'Cette notification est utilisée pour tester la synchronisation.',
          type: 'info',
          status: 'unread',
          created_at: new Date().toISOString(),
          is_active: true
        })
        .select()
        .single();
      
      if (error || !data) {
        logger.error('Erreur lors de la création de la notification de test:', error);
        return null;
      }
      
      return data.id;
    } catch (error) {
      logger.error('Erreur lors de la création de la notification de test:', error);
      return null;
    }
  }
};

